// useAuthParams — lee los query params del flow OAuth que /authorize pasa al
// SPA cuando redirige al login portal.
//
// Origen de los params: services/auth-oidc/authorize/handler.ts los arma
// despues de validar el client + redirect_uri contra la DB. El SPA confia
// ciegamente en ellos; a nivel SPA solo validamos presencia + shape minimo.
//
// Shape:
//   /:partnerId/login?client_id=...&redirect_uri=...&state=...&code_challenge=...
//     &code_challenge_method=S256&scope=openid+profile[&nonce=...]
//
// Si faltan params → el usuario navegó al SPA sin pasar por /authorize.
// Mostramos un estado "link invalido, pedile al partner que te redirija".

import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface AuthParams {
  client_id: string;
  redirect_uri: string;
  state: string;
  code_challenge: string;
  code_challenge_method: 'S256';
  scope: string;
  nonce?: string;
}

export type AuthParamsResult =
  | { ok: true; params: AuthParams }
  | { ok: false; missing: string[] };

const REQUIRED_KEYS = [
  'client_id',
  'redirect_uri',
  'state',
  'code_challenge',
  'code_challenge_method',
  'scope',
] as const;

export function useAuthParams(): AuthParamsResult {
  const [search] = useSearchParams();

  // Identidad ESTABLE entre renders: el resultado se memoiza sobre `search`
  // (que react-router ya memoiza por location.search). Sin esto, cada render
  // devolvia un objeto nuevo, y cualquier efecto con `authz` en las deps se
  // re-disparaba en cada render. En Signup eso re-ejecutaba los fetch de
  // config en cada tecla: el cache HTTP lo disfrazaba de "poll de 60s"
  // (max-age=60 de /tos) y en webviews sin cache (TVs) pegaba a la red a
  // velocidad de render — la fuente de los 3,1M req/dia a /tos en prod.
  return useMemo(() => {
    const missing: string[] = [];
    const values: Record<string, string> = {};

    for (const key of REQUIRED_KEYS) {
      const v = search.get(key);
      if (!v) missing.push(key);
      else values[key] = v;
    }

    if (missing.length > 0) {
      return { ok: false, missing } satisfies AuthParamsResult;
    }

    // TypeScript: tras validar que estan presentes, las claves existen.
    const params: AuthParams = {
      client_id: values['client_id']!,
      redirect_uri: values['redirect_uri']!,
      state: values['state']!,
      code_challenge: values['code_challenge']!,
      code_challenge_method: 'S256',
      scope: values['scope']!,
    };
    const nonce = search.get('nonce');
    if (nonce) params.nonce = nonce;

    return { ok: true, params } satisfies AuthParamsResult;
  }, [search]);
}
